/**
 * Top-level auth wrapper that selects the correct provider shell
 * based on REACT_APP_AUTH_PROVIDER.
 *
 * - clerk  -> ClerkProvider (from @clerk/clerk-react)
 * - oidc   -> thin React context (tokens come from oidc-client-ts)
 * - local  -> thin React context (tokens come from /auth/login API)
 */

import React, { ReactNode } from 'react';
import { ClerkProvider } from '@clerk/clerk-react';
import { AUTH_PROVIDER } from '../../config/auth';

/* ---------- Clerk ---------- */

const ClerkWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const publishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
  if (!publishableKey) {
    throw new Error(
      'REACT_APP_CLERK_PUBLISHABLE_KEY is not set. Configure Clerk in .env.',
    );
  }

  return (
    <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/">
      {children}
    </ClerkProvider>
  );
};

/* ---------- Pass-through (OIDC & Local) ---------- */

const PassthroughWrapper: React.FC<{ children: ReactNode }> = ({ children }) => (
  <>{children}</>
);

/* ---------- Selector ---------- */

const AuthProviderWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  if (AUTH_PROVIDER === 'clerk') {
    return <ClerkWrapper>{children}</ClerkWrapper>;
  }
  // OIDC and local modes don't need an external provider shell —
  // auth state is managed entirely inside their respective AuthContexts.
  return <PassthroughWrapper>{children}</PassthroughWrapper>;
};

export default AuthProviderWrapper;
